import { Box, IconButton, Typography } from "@mui/material";
import moment1 from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import { getUserAccountsProjectsAction } from "../Actions/CommonActions";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import {
  getProjectCompletedQueueAction,
  getProjectQueueAction,
} from "../Projects/Store/projects.action";

function ProjectQueue() {
  const dispatch = useDispatch();
  const timezone = moment1.tz.guess();
  const reducer: any = useSelector((state) => state);
  const {
    AccountReducer: { constantData, selectedAccount },
    ProjectReducer: { projectList },
  } = reducer;
  const [data, setData] = useState([]) as any;
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [gateways, setGateways] = useState([]);

  const parseRequestReceived = (obj: Record<string, number> | null) => {
    if (!obj) return {};
    const counts: any = {};
    const today = moment1().format("YYYY_MM_DD");

    Object.entries(obj).forEach(([key, value]) => {
      const [projectId, year, month, day] = key.split("_");
      const keyDate = `${year}_${month}_${day}`;

      if (keyDate !== today) return;
      if (!projectId) return;
      counts[projectId] = (counts[projectId] || 0) + (value || 0);
    });
    return counts;
  };

  const mergeAllData = (queuedProjects, completedProjects, requestsMap) => {
    const projectCounts = queuedProjects?.project_wise_count || {};

    const merged = Object.entries(projectCounts).map(([projectId, count]) => {
      const projectDetail =
        projectList?.find((p) => p?._id === projectId) || {};
      const project = completedProjects.find((p) => p._id === projectId);

      return {
        projectId,
        ...project,
        projectName: projectDetail?.projectName || "-",
        count: count ?? 0,
        callCount: project?.callCount ?? 0,
        gateway: project?.gateway ?? "-",
        requestsReceived: requestsMap?.[projectId] ?? 0,
      };
    });
    const filtered = merged.filter(
      (item) =>
        item.count !== 0 || item.callCount !== 0 || item.requestsReceived !== 0
    );

    filtered.sort((a, b) => b.count - a.count);
    return filtered;
  };

  const updateQueueData = (queuedProjects, previousData) => {
    const projectCounts = queuedProjects?.project_wise_count || {};
    const updatedData = previousData
      .map((item) => {
        const newCount = projectCounts[item.projectId];
        return {
          ...item,
          count: newCount,
          requestsReceived: item?.requestsReceived ?? 0,
        };
      })
      .filter(
        (item) =>
          item.count !== 0 ||
          item.callCount !== 0 ||
          item.requestsReceived !== 0
      );

    updatedData.sort((a, b) => b.count - a.count);
    return updatedData;
  };

  const fetchInitialData = async (spin = false) => {
    if (spin) setLoading(true);

    const params = {
      timezone,
      date: new Date().toISOString().split("T")[0],
    };

    const [queueRes, completedRes, requestReceivedRes]: any = await Promise.all(
      [
        dispatch(getProjectQueueAction()),
        dispatch(getProjectCompletedQueueAction(params)),
        dispatch(getUserAccountsProjectsAction(selectedAccount?.accountUrl)),
      ]
    );

    const parsedRequests = parseRequestReceived(requestReceivedRes || {});

    setData(() =>
      mergeAllData(queueRes || {}, completedRes || [], parsedRequests)
    );
    setLoading(false);
  };

  const fetchQueueData = async () => {
    const queueRes: any = await dispatch(getProjectQueueAction());
    setData((prev) => updateQueueData(queueRes || {}, prev || []));
  };

  const fetchRequestsData = async () => {
    const requestReceivedRes: any = await dispatch(
      getUserAccountsProjectsAction(selectedAccount?.accountUrl)
    );
    const parsedRequests = parseRequestReceived(requestReceivedRes || {});
    setData((prev: any[]) =>
      (prev || []).map((row) => ({
        ...row,
        requestsReceived: parsedRequests[row.projectId] ?? 0,
      }))
    );
  };

  useEffect(() => {
    setGateways(constantData?.gateways);

    if (projectList?.length > 0) fetchInitialData(true);

    const queueInterval = setInterval(() => fetchQueueData(), 15000);
    const requestsInterval = setInterval(() => fetchRequestsData(), 10000);

    return () => {
      clearInterval(queueInterval);
      clearInterval(requestsInterval);
    };
  }, [projectList, selectedAccount?.accountUrl]);

  const refreshData = async () => {
    setRefreshLoading(true);
    await fetchInitialData();
    setRefreshLoading(false);
  };

  return (
    <>
      <Box className="flex-between-only">
        <Typography className="font-20-grey my-2">
          Today's Project wise data
        </Typography>
        <Box className="flex-between-only gap-1" sx={{ height: 36 }}>
          <IconButton
            title="Refresh"
            onClick={refreshData}
            sx={{
              ...commonStyles.refreshButtonStyle,
              ...dashboardStyles.refreshAnimation(refreshLoading),
            }}
            disabled={refreshLoading}
          >
            <RefreshIcon className="refresh-icon" height={14} width={14} />
          </IconButton>
        </Box>
      </Box>

      <DynamicTable
        columns={[
          {
            label: "Project Name",
            field: "projectName",
          },
          {
            label: "Project Id",
            field: "projectId",
          },
          {
            label: "Gateway Selected",
            render: (row) => {
              const gateway: any = gateways?.find(
                ({ value }) => value + "_ultra_prod" === row?.gateway
              );

              return gateway?.name
                ? `${gateway?.name || ""} / ${row?.gateway || ""}`
                : "-";
            },
          },
          {
            label: "Requests Received",
            render: (row) => (
              <div style={{ background: "#60A5FA", padding: "8px 8px" }}>
                {row?.requestsReceived ?? 0}
              </div>
            ),
          },
          {
            label: "Calls in Queue",
            render: (row) => (
              <div style={{ background: "#F59E0B", padding: "8px 8px" }}>
                {row?.count}
              </div>
            ),
          },
          {
            label: "Calls Completed",
            render: (row) => (
              <div
                style={{
                  background: "#22C55E",
                  padding: "8px 8px",
                }}
              >
                {row?.callCount}
              </div>
            ),
          },
        ]}
        data={data}
        loading={loading}
        rowCount={data?.length}
      />
    </>
  );
}

export default ProjectQueue;
